import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, ChevronDown, ChevronUp, Rocket, Database, ShoppingCart, Landmark, Heart, Plane, Briefcase } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';

const categories = [
  { id: 'all', label: 'All', icon: Building2 },
  { id: 'ai', label: 'Enterprise AI', icon: Rocket },
  { id: 'commerce', label: 'Commerce', icon: ShoppingCart },
  { id: 'enterprise', label: 'Enterprise', icon: Briefcase },
  { id: 'financial', label: 'Financial', icon: Landmark },
  { id: 'healthcare', label: 'Healthcare', icon: Heart },
  { id: 'technology', label: 'Technology', icon: Plane },
];

const experienceData = [
  {
    id: 'compassx',
    company: 'CompassX',
    role: 'Enterprise AI Practice Lead',
    category: 'ai',
    summary: 'Built and launched the CompassX Enterprise AI Practice, defining the firm\'s methodology for helping organizations operationalize AI across engineering, product, and operational teams.',
    details: [
      'Designed enterprise AI governance operating models enabling companies to safely move from AI experimentation into scalable production AI environments.',
      'Developed enterprise frameworks for AI intake, prioritization, and lifecycle governance, allowing leadership teams to manage AI initiatives as a structured portfolio.',
      'Architected AI solution frameworks integrating LLMs, enterprise data platforms, vector databases, and orchestration systems into existing enterprise ecosystems.',
      'Guided executive leadership teams in defining AI transformation roadmaps, aligning AI initiatives with measurable operational outcomes and ROI targets.',
    ],
  },
  {
    id: 'infoblox',
    company: 'Infoblox',
    role: 'Enterprise AI Transformation',
    category: 'ai',
    summary: 'Led architecture and strategy for Infoblox\'s enterprise AI transformation initiative, defining how AI would be integrated across engineering, product, and corporate operations.',
    details: [
      'Designed the AI Tower governance model, establishing centralized AI intake, prioritization, and oversight to ensure AI initiatives aligned with enterprise strategy.',
      'Architected BloxMate, an internal AI assistant designed to support HR, IT, and operational workflows by providing contextual enterprise knowledge access.',
      'Designed the Hero Agent architecture enabling AI agents to securely interact with internal systems while maintaining enterprise security and data governance.',
      'Established responsible AI guardrails including human oversight frameworks, model transparency requirements, and enterprise audit logging standards.',
    ],
  },
  {
    id: 'vertiv',
    company: 'Vertiv',
    role: 'AI-Driven Engineering & Manufacturing Transformation',
    category: 'ai',
    summary: 'Led architecture design for AI-enabled operational systems supporting a global data center infrastructure manufacturer with complex engineering and manufacturing requirements.',
    details: [
      'Designed an AI-powered sales configuration platform capable of generating complex bills of material for large-scale infrastructure deployments.',
      'Architected systems enabling visibility across the entire order-to-manufacture lifecycle, from product configuration through manufacturing and delivery.',
      'Built engineering data pipelines allowing AI-driven insights to support production planning and supply chain coordination across global operations.',
      'Implemented executive operational dashboards allowing leadership to monitor engineering throughput, manufacturing timelines, and supply chain risks.',
    ],
  },
  {
    id: 'qdoba',
    company: 'QDOBA',
    role: 'Enterprise Data & AI Modernization',
    category: 'ai',
    summary: 'Designed the EDW 2.0 enterprise data platform architecture enabling scalable cloud-based analytics infrastructure supporting both franchise and corporate operations.',
    details: [
      'Built enterprise data product frameworks allowing teams to develop reusable data assets across finance, operations, and marketing teams.',
      'Implemented AI-enabled reporting systems capable of automatically generating insights from operational data for executive decision making.',
      'Established enterprise data governance practices supporting data quality, lineage tracking, and secure AI access to enterprise data assets.',
      'Designed operational dashboards allowing executives to track store performance, franchise health, and customer engagement metrics in real-time.',
    ],
  },
  {
    id: 'tiktok',
    company: 'TikTok',
    role: 'Live Commerce Infrastructure',
    category: 'commerce',
    summary: 'Designed commerce technology architecture supporting TikTok live selling environments, enabling seamless product discovery and purchasing during live stream broadcasts.',
    details: [
      'Built integrations connecting product catalogs with live stream sales workflows, enabling real-time product showcasing during broadcasts.',
      'Developed analytics systems tracking viewer engagement, purchase conversion, and creator-driven sales performance across campaigns.',
      'Implemented inventory management systems ensuring product availability synchronization with live selling events across the platform.',
      'Designed checkout flow optimizations reducing friction in the live commerce purchase experience and improving conversion rates.',
    ],
  },
  {
    id: 'ebay',
    company: 'eBay Live',
    role: 'Live Auction Platform',
    category: 'commerce',
    summary: 'Implemented integrations enabling real-time product bidding and auction capabilities during live selling events, connecting sellers with highly engaged buyer audiences.',
    details: [
      'Designed operational analytics dashboards allowing sellers to monitor real-time auction performance and buyer engagement metrics.',
      'Built bidding system integrations enabling seamless auction experiences within live streaming environments for sellers and buyers.',
      'Implemented seller tools for managing auction inventory, reserve prices, and buyer communications during live selling events.',
      'Developed performance tracking systems measuring auction success rates, average selling prices, and buyer retention metrics.',
    ],
  },
  {
    id: 'whatnot',
    company: 'Whatnot',
    role: 'Marketplace Integration',
    category: 'commerce',
    summary: 'Built marketplace integrations allowing product inventory to synchronize automatically across live commerce platforms, enabling efficient multi-channel selling operations.',
    details: [
      'Developed seller analytics tools enabling performance monitoring and marketplace growth insights across all product categories.',
      'Implemented inventory synchronization systems ensuring accurate product counts and pricing across all marketplace listings.',
      'Built automated listing tools reducing manual effort required to manage products across multiple selling channels efficiently.',
      'Designed reporting dashboards tracking seller performance, revenue trends, and customer engagement metrics over time.',
    ],
  },
  {
    id: 'shopify',
    company: 'Shopify',
    role: 'E-Commerce Architecture',
    category: 'commerce',
    summary: 'Designed scalable Shopify architecture supporting high-volume live commerce operations with complex inventory management and multi-channel fulfillment requirements.',
    details: [
      'Implemented multi-application integrations enabling automated order processing, product catalog synchronization, and fulfillment management.',
      'Built custom inventory collection systems allowing products to be managed across multiple live commerce platforms seamlessly.',
      'Designed checkout optimizations improving conversion rates and reducing cart abandonment during high-traffic selling events.',
      'Developed custom apps extending Shopify functionality to support unique live commerce business requirements and workflows.',
    ],
  },
  {
    id: 'rogers',
    company: 'Rogers Communications',
    role: 'Enterprise Data Modernization',
    category: 'enterprise',
    parent: 'Slalom',
    summary: 'Led enterprise data modernization initiative migrating telecom data infrastructure to cloud-based analytics platforms supporting millions of customers across Canada.',
    details: [
      'Designed enterprise data lake architecture enabling large-scale analytics and AI workloads across customer, network, and operational data.',
      'Directed cross-functional teams responsible for migrating enterprise data pipelines and operational reporting systems to cloud infrastructure.',
      'Implemented data governance frameworks ensuring data quality, security, and compliance across all migrated systems and platforms.',
      'Built executive dashboards providing real-time visibility into network performance, customer metrics, and operational KPIs.',
    ],
  },
  {
    id: 'bell',
    company: 'Bell Canada',
    role: 'Telecom Data Transformation',
    category: 'enterprise',
    parent: 'Slalom',
    summary: 'Supported telecom data transformation initiatives enabling improved operational analytics and data-driven decision making across all major business units.',
    details: [
      'Implemented enterprise governance frameworks ensuring consistent data quality and analytics access across all business units.',
      'Designed data integration patterns connecting disparate telecom systems into unified analytics environments for reporting.',
      'Built operational reporting systems enabling real-time monitoring of network performance and customer service metrics.',
      'Established data stewardship practices ensuring ongoing data quality and compliance with regulatory requirements.',
    ],
  },
  {
    id: 'ulta',
    company: 'Ulta Beauty',
    role: 'Enterprise Agile Transformation',
    category: 'enterprise',
    parent: 'Slalom',
    summary: 'Led enterprise Agile transformation across more than 40 product engineering teams, significantly improving delivery performance and cross-team collaboration.',
    details: [
      'Embedded Agile coaching across engineering organizations to improve delivery performance, collaboration, and predictability.',
      'Established product engineering governance frameworks supporting faster and more predictable software delivery cycles.',
      'Designed metrics and reporting systems allowing leadership to track delivery health across all engineering teams.',
      'Implemented scaled Agile practices enabling coordination across multiple teams working on shared product initiatives.',
    ],
  },
  {
    id: 'arkansas',
    company: 'State of Arkansas',
    role: 'Statewide Digital Transformation',
    category: 'enterprise',
    parent: 'Slalom',
    summary: 'Led statewide digital transformation initiative modernizing citizen services infrastructure across multiple state agencies and government departments.',
    details: [
      'Designed enterprise case management platform used across multiple state agencies to deliver citizen services more efficiently.',
      'Coordinated large consulting teams delivering statewide technology modernization across complex stakeholder environments.',
      'Implemented integration patterns connecting legacy state systems with modern digital service delivery platforms.',
      'Established governance frameworks ensuring consistent delivery standards and quality across all modernization initiatives.',
    ],
  },
  {
    id: 'eiq',
    company: 'EIQ',
    role: 'Enterprise Data Intelligence Platform',
    category: 'financial',
    parent: 'Bank of America',
    summary: 'Designed enterprise AI-driven data governance platform used to monitor data quality and regulatory compliance across large-scale financial data systems.',
    details: [
      'Implemented anomaly detection algorithms identifying data inconsistencies across large-scale enterprise data pipelines in real-time.',
      'Built enterprise metadata management frameworks enabling traceability and lineage tracking across financial data systems.',
      'Designed data quality scoring systems providing visibility into data health across critical business processes and reporting.',
      'The platform architecture became an early foundation for IBM Watson data intelligence capabilities and enterprise AI.',
    ],
  },
  {
    id: 'agileview',
    company: 'AgileView',
    role: 'Portfolio Management Platform',
    category: 'financial',
    parent: 'Bank of America',
    summary: 'Designed enterprise portfolio management platform for monitoring engineering delivery performance across large-scale technology organizations and initiatives.',
    details: [
      'Built executive dashboards providing real-time insights into program status, delivery health, and operational risk across initiatives.',
      'Enabled leadership teams to identify delivery bottlenecks and improve strategic program oversight through data-driven insights.',
      'Designed reporting systems tracking resource allocation, budget utilization, and delivery velocity across the entire portfolio.',
      'Implemented predictive analytics helping leadership anticipate delivery risks and take proactive corrective actions early.',
    ],
  },
  {
    id: 'boaapp',
    company: 'Bank of America App',
    role: 'Mobile Banking Application',
    category: 'financial',
    parent: 'Bank of America',
    summary: 'Contributed to architecture and development of the Bank of America mobile banking application serving millions of customers with secure financial services.',
    details: [
      'Built features supporting mobile banking workflows and customer financial management across checking, savings, and investments.',
      'Designed integrations connecting mobile experience with core banking systems ensuring real-time account information accuracy.',
      'Implemented security features ensuring customer data protection and compliance with financial services regulations.',
      'Developed performance optimizations improving app responsiveness and user experience across various device types.',
    ],
  },
  {
    id: 'salt',
    company: 'SALT',
    role: 'Enterprise Application',
    category: 'financial',
    parent: 'Bank of America',
    summary: 'Led development of SALT enterprise application supporting internal banking operations, business process automation, and operational workflow management.',
    details: [
      'Designed and delivered enterprise application supporting operational workflows across multiple banking divisions and teams.',
      'Built integrations with existing Bank of America systems and data platforms ensuring seamless data flow across systems.',
      'Implemented user interface designs optimizing employee productivity and reducing manual processing time significantly.',
      'Developed reporting capabilities providing management visibility into operational metrics and performance indicators.',
    ],
  },
  {
    id: 'usaa',
    company: 'USAA',
    role: 'Enterprise Integration',
    category: 'financial',
    summary: 'Implemented enterprise integration systems connecting legacy financial systems with modern platforms serving military members and their families nationwide.',
    details: [
      'Built secure APIs enabling banking and insurance systems interoperability across multiple product lines and services.',
      'Designed integration patterns ensuring data consistency and real-time synchronization between legacy and modern systems.',
      'Implemented security frameworks meeting stringent financial services compliance and data protection requirements.',
      'Developed monitoring systems providing visibility into integration health and performance across all connected systems.',
    ],
  },
  {
    id: 'uhc',
    company: 'United Healthcare',
    role: 'Healthcare Technology Transformation',
    category: 'healthcare',
    summary: 'Designed prescription management automation platform streamlining pharmacy refill workflows and improving patient medication adherence across healthcare networks.',
    details: [
      'Led technology integration efforts supporting healthcare acquisitions and system consolidation across multiple business units.',
      'Implemented Salesforce-based workflow systems supporting operational healthcare teams and improving care coordination.',
      'Guided engineering teams through transition from waterfall to Agile software delivery practices, improving velocity.',
      'Built analytics systems tracking prescription fulfillment metrics and identifying opportunities for process improvement.',
    ],
  },
  {
    id: 'ucfit',
    company: 'UCFit',
    role: 'Digital Wellness Platform',
    category: 'healthcare',
    summary: 'Designed digital engagement platform supporting personalized fitness and wellness programs for enterprise employee wellness and consumer health audiences.',
    details: [
      'Built analytics systems allowing organizations to track user engagement, program effectiveness, and health outcomes.',
      'Implemented mobile-first digital health platform architecture supporting scalable wellness services across web and mobile.',
      'Designed personalization algorithms delivering customized fitness recommendations based on user goals and activity patterns.',
      'Developed integration capabilities connecting wellness platform with employer HR systems and health insurance providers.',
    ],
  },
  {
    id: 'delta',
    company: 'FlyDelta - Delta Airlines',
    role: 'Mobile Application Development',
    category: 'technology',
    summary: 'Contributed to architecture and development of the FlyDelta mobile application, improving the customer travel experience for millions of passengers worldwide.',
    details: [
      'Built integrations connecting airline operations systems with mobile travel management tools enabling real-time flight info.',
      'Designed backend services supporting flight tracking, boarding passes, and travel notifications across iOS and Android.',
      'Implemented features enabling customers to manage bookings, check-in, and access airport services through mobile devices.',
      'Developed performance optimizations ensuring reliable app functionality during high-traffic travel periods and peak times.',
    ],
  },
  {
    id: 'carvana',
    company: 'Carvana',
    role: 'Automotive E-Commerce Platform',
    category: 'technology',
    summary: 'Supported architecture of a large-scale automotive e-commerce platform enabling online vehicle purchasing and revolutionizing the car buying experience.',
    details: [
      'Built integrations connecting logistics, financing, and inventory systems supporting end-to-end vehicle purchasing workflows.',
      'Designed systems enabling customers to browse, finance, and purchase vehicles entirely online with home delivery options.',
      'Implemented inventory management systems tracking vehicle availability, pricing, and location across distribution network.',
      'Developed customer experience features streamlining the vehicle selection and purchase process for all users.',
    ],
  },
  {
    id: 'kuiper',
    company: 'Amazon Project Kuiper',
    role: 'Satellite Infrastructure',
    category: 'technology',
    summary: 'Contributed to infrastructure systems supporting global satellite internet connectivity initiatives connecting underserved communities around the world.',
    details: [
      'Built operational monitoring systems supporting satellite network deployment and performance tracking across regions.',
      'Designed infrastructure components enabling reliable satellite communication across global coverage areas worldwide.',
      'Implemented analytics systems tracking network performance, coverage quality, and service availability metrics.',
      'Developed automation tools supporting satellite constellation management and operational efficiency improvements.',
    ],
  },
  {
    id: 'salesforce',
    company: 'Salesforce',
    role: 'Enterprise CRM Integrations',
    category: 'technology',
    summary: 'Designed enterprise CRM integrations supporting sales operations across multiple industries with complex business requirements and workflow automation needs.',
    details: [
      'Built automation systems connecting CRM platforms with operational and financial systems enabling end-to-end automation.',
      'Designed integration patterns ensuring data synchronization between Salesforce and enterprise backend systems.',
      'Implemented custom Salesforce solutions extending platform capabilities to meet unique business requirements.',
      'Developed reporting and analytics dashboards providing sales leadership visibility into pipeline and performance.',
    ],
  },
  {
    id: 'wholefoods',
    company: 'Whole Foods',
    role: 'Retail Systems Integration',
    category: 'technology',
    summary: 'Delivered retail systems integrations improving operational analytics and e-commerce capabilities for premium grocery retail operations across store locations.',
    details: [
      'Built systems connecting supply chain data with store-level performance metrics enabling inventory optimization.',
      'Designed integration patterns connecting point-of-sale systems with enterprise analytics platforms for reporting.',
      'Implemented operational dashboards providing store managers visibility into sales, inventory, and customer metrics.',
      'Developed data pipelines enabling real-time reporting on product movement and store performance across locations.',
    ],
  },
  {
    id: 'dod',
    company: 'Department of Defense',
    role: 'Secure Enterprise Systems',
    category: 'technology',
    summary: 'Contributed to secure enterprise systems supporting mission-critical operations with stringent security requirements and compliance standards.',
    details: [
      'Designed integration architecture supporting interoperability between defense technology platforms and systems.',
      'Implemented security frameworks meeting DoD compliance requirements and data protection standards thoroughly.',
      'Built operational systems supporting mission planning, resource management, and communications workflows.',
      'Developed monitoring and alerting systems ensuring system reliability and rapid incident response capabilities.',
    ],
  },
  {
    id: 'dot',
    company: 'Department of Transportation',
    role: 'Infrastructure Systems',
    category: 'technology',
    summary: 'Delivered operational systems supporting statewide transportation infrastructure management and public safety initiatives across transportation networks.',
    details: [
      'Built analytics systems enabling real-time monitoring of infrastructure health and transportation performance metrics.',
      'Designed integration patterns connecting traffic management systems with central monitoring platforms for visibility.',
      'Implemented reporting dashboards providing transportation officials visibility into road conditions and traffic patterns.',
      'Developed predictive maintenance systems helping prioritize infrastructure repairs and resource allocation decisions.',
    ],
  },
  {
    id: 'elevation',
    company: 'Elevation Church',
    role: 'Digital Analytics & Growth Consultant',
    category: 'technology',
    summary: 'Provided digital analytics and growth consulting to support organizational digital transformation and audience engagement strategies across platforms.',
    details: [
      'Designed digital analytics frameworks to measure engagement, reach, and growth metrics across all digital platforms.',
      'Developed growth strategies leveraging data-driven insights to expand audience reach and community engagement.',
      'Implemented analytics systems supporting organizational decision making and resource allocation optimization.',
      'Built reporting dashboards providing leadership visibility into digital performance and audience growth trends.',
    ],
  },
];

function ExperienceTile({ item }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.2 }}
    >
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <Card 
          className={`glass-light border-0 h-full transition-all duration-300 hover:shadow-lg ${isOpen ? 'ring-1 ring-teal/30' : ''}`}
          data-testid={`experience-tile-${item.id}`}
        >
          <CollapsibleTrigger className="w-full text-left">
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h4 className="font-heading font-semibold text-foreground">
                      {item.company}
                    </h4>
                    {item.parent && (
                      <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                        via {item.parent}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-teal font-medium mb-2">
                    {item.role}
                  </p>
                  <p className="text-sm text-muted-foreground line-clamp-3">
                    {item.summary}
                  </p>
                </div>
                <div className="p-2 rounded-lg bg-teal/10 shrink-0">
                  {isOpen ? (
                    <ChevronUp className="w-4 h-4 text-teal" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-teal" />
                  )}
                </div>
              </div>
            </CardContent>
          </CollapsibleTrigger>
          
          <CollapsibleContent>
            <div className="px-5 pb-5">
              <div className="border-t border-border pt-4">
                <ul className="space-y-2">
                  {item.details.map((detail, idx) => (
                    <motion.li
                      key={idx}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="flex items-start gap-2 text-sm text-foreground/80"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-orange mt-2 shrink-0" />
                      {detail}
                    </motion.li>
                  ))}
                </ul>
              </div>
            </div>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </motion.div>
  );
}

export function TimelineSection() {
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredExperience = activeCategory === 'all' 
    ? experienceData 
    : experienceData.filter(item => item.category === activeCategory);

  return (
    <section
      id="experience"
      data-testid="timeline-section"
      className="py-20 lg:py-32 bg-muted/30"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <p className="text-teal font-mono text-sm uppercase tracking-widest mb-4">
            Career Journey
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-6">
            Professional Experience
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-4">
            Enterprise technology executive and AI transformation leader with 15+ years 
            delivering large-scale digital modernization across Fortune 500 enterprises, 
            startups, and emerging commerce platforms.
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-teal to-orange mx-auto rounded-full" />
        </motion.div>

        {/* Filter Pills */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-wrap justify-center gap-2 mb-10"
        >
          {categories.map((cat) => {
            const Icon = cat.icon;
            const isActive = activeCategory === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                data-testid={`filter-${cat.id}`}
                className={`
                  px-4 py-2 rounded-full text-sm font-medium transition-all duration-300
                  flex items-center gap-2
                  ${isActive 
                    ? 'bg-teal text-white shadow-lg shadow-teal/25' 
                    : 'bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground'
                  }
                `}
              >
                <Icon className="w-4 h-4" />
                {cat.label}
                {cat.id !== 'all' && (
                  <span className={`text-xs ${isActive ? 'text-white/70' : 'text-muted-foreground'}`}>
                    ({experienceData.filter(e => e.category === cat.id).length})
                  </span>
                )}
              </button>
            );
          })}
        </motion.div>

        {/* Experience Grid */}
        <motion.div 
          layout
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          <AnimatePresence mode="popLayout">
            {filteredExperience.map((item) => (
              <ExperienceTile key={item.id} item={item} />
            ))}
          </AnimatePresence>
        </motion.div>

        {/* Count indicator */}
        <motion.p 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center text-sm text-muted-foreground mt-8"
        >
          Showing {filteredExperience.length} of {experienceData.length} engagements
        </motion.p>
      </div>
    </section>
  );
}
